import { formatCurrency, useSessionStore, useVaultStore } from "@solomon-apps/shared";
import { TOKEN_DECIMALS } from "@solomon-apps/shared";
import type { BarSegment } from "@solomon-labs/design/components/data";
import { GRAPH_COLORS } from "@solomon-labs/design/tokens/graph";
import React from "react";

import { ASSET_CATEGORY_TO_ASSETS, type AssetCategory } from "@/config/assetCategoryMapping";
import { useStakingStats } from "@/hooks/staking";
import type { ReserveTableGroup } from "@/pages/Stats/types";

import { useOffApiTreasuryBalances } from "./useOffApiTreasuryBalances";
import { useProtocolData } from "./useProtocolData";

function parseAmount(value: string | number | null | undefined): number {
    if (value === null || value === undefined) return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function formatTokenNumber(amount: bigint | undefined, decimals: number): string {
    if (!amount) return "0";
    const divisor = BigInt(10 ** decimals);
    const whole = amount / divisor;
    const remainder = amount % divisor;
    // Preserve full fractional precision so the downstream Intl-based
    // formatter (formatCurrency) rounds to 2dp — slicing here would
    // truncate, producing e.g. $438,531.96 instead of $438,531.97.
    const decimal = remainder.toString().padStart(decimals, "0");
    return `${whole}.${decimal}`;
}

function formatPercentOfTvl(value: number, total: number): string {
    if (total <= 0) return "0.0%";
    return `${((value / total) * 100).toFixed(1)}%`;
}

function addAmount(map: Map<string, number>, key: string, amount: number): void {
    if (amount <= 0) return;
    map.set(key, (map.get(key) ?? 0) + amount);
}

/** Catch-all class for a holding no entry in `ASSET_CATEGORY_TO_ASSETS` claims.
 *  Present so a newly reported symbol shows up as an unclassified row rather
 *  than silently dropping out of the composition and its total. */
const UNCLASSIFIED_CLASS = "Unclassified";

/** Where one asset physically sits: a custodian, or an onchain program. */
export interface ReserveVenueRow {
    label: string;
    amount: number;
    amountDisplay: string;
    /** What the holding is ultimately exposure to, when the token wraps
     *  something else (USDG at Paxos wraps U.S. Treasury Bonds). */
    underlying?: string;
    /** Onchain account a reader can check independently. */
    verifyAddress?: string;
}

/** One asset within a class, and every venue it is held at. */
export interface ReserveAssetRow {
    symbol: string;
    amount: number;
    amountDisplay: string;
    /** Share of reported reserves, one decimal. */
    share: string;
    venues: ReserveVenueRow[];
}

/** Top level of the composition: an asset class and the assets under it. */
export interface ReserveClassRow {
    label: string;
    amount: number;
    amountDisplay: string;
    /** Share of reported reserves, one decimal. */
    share: string;
    assets: ReserveAssetRow[];
}

export interface ReservesData {
    collateralRatio: string;
    protocolTvl: string;
    protocolTvlNumber: number;
    mintedDisplay: string;
    stakedDisplay: string;
    stakedNumber: number | null;
    /** Sum of every itemized holding below, and nothing else. Derivatives
     *  notional is deliberately excluded: it measures market exposure, not an
     *  owned asset. This is NOT the protocol's total reserve value, because
     *  the data feed does not itemize every reserve account. */
    reportedReserves: number;
    reportedReservesDisplay: string;
    /** class -> asset -> venue, one hierarchy over the same dollars. */
    reserveClasses: ReserveClassRow[];
    locationSegments: BarSegment[];
    locationGroups: ReserveTableGroup[];
    makeupSegments: BarSegment[];
    makeupGroups: ReserveTableGroup[];
    isLoading: boolean;
}

export function useReservesData(): ReservesData {
    const { data: protocolData, isLoading } = useProtocolData();
    const { data: stakingStats, isLoading: isStatsLoading } = useStakingStats();
    const tokens = useSessionStore((s) => s.tokens);
    const { vaultTokenSupply } = useVaultStore();
    const offApiPositions = useOffApiTreasuryBalances();

    const protocolTvlFormatted = protocolData?.protocolTvl ? formatCurrency(protocolData.protocolTvl) : "—";
    const protocolTvlNumber = parseAmount(protocolData?.protocolTvl);

    const totalReserves =
        parseAmount(protocolData?.custodyNotionalUsd) +
        parseAmount(protocolData?.vaultNotionalUsd) +
        parseAmount(protocolData?.yieldDistributorsNotionalUsd) +
        parseAmount(protocolData?.reserveFundNotionalUsd) +
        parseAmount(protocolData?.positionsNotionalUsd);

    const collateralRatio = protocolTvlNumber > 0 ? `${((totalReserves / protocolTvlNumber) * 100).toFixed(1)}%` : "—";

    const mintedDisplay = React.useMemo(() => {
        try {
            const usdv = tokens.find((token) => token.symbol === "USDv");
            if (vaultTokenSupply && usdv) {
                return formatCurrency(formatTokenNumber(vaultTokenSupply.toBigint(), TOKEN_DECIMALS.USDv));
            }
        } catch (_error) {
            // Fall back to protocol data below.
        }

        return protocolData?.protocolTvl ? formatCurrency(protocolData.protocolTvl) : "—";
    }, [protocolData?.protocolTvl, tokens, vaultTokenSupply]);

    // Active-staked only — excludes the cooldown/unstaking pool. The
    // /staking/stats endpoint splits `totalStaked` (active) from
    // `totalUnstaking` (cooldown); `protocolData.stakedTvl` and the
    // on-chain `totalAssets` lump them together, so neither is usable here.
    const stakedDisplay = React.useMemo(() => {
        const raw = stakingStats?.totalStaked;
        if (!raw) return "—";
        try {
            return formatCurrency(formatTokenNumber(BigInt(raw), TOKEN_DECIMALS.USDv));
        } catch (_error) {
            return "—";
        }
    }, [stakingStats?.totalStaked]);

    /** The same figure as `stakedDisplay`, unformatted, so a caller can state
     *  it as a share of supply without re-parsing the currency string. */
    const stakedNumber = React.useMemo(() => {
        const raw = stakingStats?.totalStaked;
        if (!raw) return null;
        try {
            const parsed = Number(formatTokenNumber(BigInt(raw), TOKEN_DECIMALS.USDv));
            return Number.isFinite(parsed) ? parsed : null;
        } catch (_error) {
            return null;
        }
    }, [stakingStats?.totalStaked]);

    const { locationGroups, locationSegments, makeupGroups, makeupSegments, reserveClasses, reportedReserves } =
        React.useMemo(() => {
            const onchainTotals = new Map<string, number>();
            const custodyTotals = new Map<string, number>();
            const totalsByAsset = new Map<string, Map<string, number>>();
            // `${symbol}|${venue}` -> the extra facts only the off-API config
            // carries. The data API returns no underlying or wallet per holding.
            const venueMeta = new Map<string, { underlying?: string; verifyAddress?: string }>();

            const addAssetSource = (
                asset: string | null | undefined,
                source: string,
                amountUsd: string | number | null | undefined
            ): void => {
                const symbol = (asset ?? "").trim().toUpperCase();
                const amount = parseAmount(amountUsd);
                if (!symbol || amount <= 0) return;

                const assetSources = totalsByAsset.get(symbol) ?? new Map<string, number>();
                addAmount(assetSources, source, amount);
                totalsByAsset.set(symbol, assetSources);
            };

            // Every onchain holding is attributed to its asset, not just the
            // stablecoins: an asset that no category claims surfaces under
            // `UNCLASSIFIED_CLASS` rather than vanishing from the totals.
            for (const item of protocolData?.vault ?? []) {
                addAmount(onchainTotals, "Vault", parseAmount(item.amountUsd));
                addAssetSource(item.asset, "Vault", item.amountUsd);
            }

            for (const item of protocolData?.yieldDistributors ?? []) {
                addAmount(onchainTotals, "Yield Distributor", parseAmount(item.amountUsd));
                addAssetSource(item.asset, "Yield Distributor", item.amountUsd);
            }

            for (const item of protocolData?.reserveFund ?? []) {
                addAmount(onchainTotals, "Reserve Fund", parseAmount(item.amountUsd));
                addAssetSource(item.asset, "Reserve Fund", item.amountUsd);
            }

            for (const item of protocolData?.custody ?? []) {
                const sourceLabel = item.name?.trim() || "Unknown";
                addAmount(custodyTotals, sourceLabel, parseAmount(item.amountUsd));
                addAssetSource(item.asset, sourceLabel, item.amountUsd);
            }

            // Off-API: USDG at Paxos isn't in `protocolData.custody` yet.
            // `offApiPositions` is RPC-fetched (see
            // useOffApiTreasuryBalances). Folded into `custodyTotals` so
            // Paxos sits next to Ceffu under the single "Custodians" group
            // — the user-facing taxonomy stopped distinguishing exchange
            // custody from treasury-bill custody. Remove the off-API
            // integration once data-api ships these positions in
            // `protocolData.custody`.
            for (const p of offApiPositions) {
                addAmount(custodyTotals, p.venue, p.amountValue.toNumber());
                addAssetSource(p.asset, p.venue, p.amountValue.toNumber());
                venueMeta.set(`${p.asset.trim().toUpperCase()}|${p.venue}`, {
                    underlying: p.underlyingExposure,
                    verifyAddress: p.walletAddress
                });
            }

            const buildGroup = (label: string, totals: Map<string, number>): ReserveTableGroup => {
                const rows = Array.from(totals.entries())
                    .map(([rowLabel, amount]) => ({
                        label: rowLabel,
                        amount,
                        tvl: formatPercentOfTvl(amount, protocolTvlNumber)
                    }))
                    .sort((a, b) => b.amount - a.amount);

                return {
                    label,
                    total: formatCurrency(rows.reduce((sum, row) => sum + row.amount, 0)),
                    rows: rows.map((row) => ({
                        label: row.label,
                        amount: formatCurrency(row.amount),
                        tvl: row.tvl
                    }))
                };
            };

            const buildSegments = (groups: ReserveTableGroup[]): BarSegment[] =>
                groups.map((group, index) => ({
                    label: group.label,
                    value: parseAmount(group.total.replace(/[$,]/g, "")),
                    color: GRAPH_COLORS[index % GRAPH_COLORS.length]
                }));

            const locationGroupsResult: ReserveTableGroup[] = [
                buildGroup("Onchain", onchainTotals),
                buildGroup("Custodians", custodyTotals)
            ].filter((group) => group.rows.length > 0);

            // Asset Makeup is grouped by *asset category* (Stablecoins,
            // Basis Trades), not by per-asset → location breakdown. Each
            // category is a `ReserveTableGroup` whose `total` sums the
            // category's assets and whose `rows` list those assets (no
            // location detail — that lives in Asset Locations above).
            // Sum each asset across its sources first, then bucket the
            // totals into the category they belong to.
            const totalsByAssetFlat = new Map<string, number>();
            for (const [asset, sources] of totalsByAsset.entries()) {
                let total = 0;
                for (const amt of sources.values()) total += amt;
                if (total > 0) totalsByAssetFlat.set(asset, total);
            }
            const buildAssetCategoryGroup = (category: AssetCategory): ReserveTableGroup | null => {
                const symbols = ASSET_CATEGORY_TO_ASSETS[category];
                const rows = symbols
                    .map((sym) => ({
                        label: sym,
                        amount: totalsByAssetFlat.get(sym) ?? 0
                    }))
                    .filter((r) => r.amount > 0)
                    .sort((a, b) => b.amount - a.amount);
                if (rows.length === 0) return null;
                return {
                    label: category,
                    total: formatCurrency(rows.reduce((sum, row) => sum + row.amount, 0)),
                    rows: rows.map((row) => ({
                        label: row.label,
                        amount: formatCurrency(row.amount),
                        tvl: formatPercentOfTvl(row.amount, protocolTvlNumber)
                    }))
                };
            };
            const makeupGroupsResult: ReserveTableGroup[] = (["Stablecoins", "Basis Trades (Hedged)"] as const)
                .map(buildAssetCategoryGroup)
                .filter((g): g is ReserveTableGroup => g !== null);

            // One hierarchy over the same dollars: class, then asset, then the
            // venue holding it. Every figure below shares one denominator, the
            // sum of these holdings, so no row is counted twice and no row is
            // measured against a different total than its neighbour.
            const reportedReservesTotal = Array.from(totalsByAsset.values()).reduce(
                (sum, sources) => sum + Array.from(sources.values()).reduce((a, b) => a + b, 0),
                0
            );

            const assetRowsBySymbol = new Map<string, ReserveAssetRow>();
            for (const [symbol, sources] of totalsByAsset.entries()) {
                const venues: ReserveVenueRow[] = Array.from(sources.entries())
                    .map(([label, amount]) => ({
                        label,
                        amount,
                        amountDisplay: formatCurrency(amount),
                        ...venueMeta.get(`${symbol}|${label}`)
                    }))
                    .sort((a, b) => b.amount - a.amount);
                const amount = venues.reduce((sum, venue) => sum + venue.amount, 0);
                if (amount <= 0) continue;
                assetRowsBySymbol.set(symbol, {
                    symbol,
                    amount,
                    amountDisplay: formatCurrency(amount),
                    share: formatPercentOfTvl(amount, reportedReservesTotal),
                    venues
                });
            }

            const claimed = new Set<string>();
            const classRows: ReserveClassRow[] = [];
            for (const category of Object.keys(ASSET_CATEGORY_TO_ASSETS) as AssetCategory[]) {
                const assets = ASSET_CATEGORY_TO_ASSETS[category]
                    .map((symbol) => assetRowsBySymbol.get(symbol.toUpperCase()))
                    .filter((row): row is ReserveAssetRow => row !== undefined)
                    .sort((a, b) => b.amount - a.amount);
                for (const row of assets) claimed.add(row.symbol);
                if (assets.length === 0) continue;
                const amount = assets.reduce((sum, row) => sum + row.amount, 0);
                classRows.push({
                    label: category,
                    amount,
                    amountDisplay: formatCurrency(amount),
                    share: formatPercentOfTvl(amount, reportedReservesTotal),
                    assets
                });
            }

            const unclassified = Array.from(assetRowsBySymbol.values())
                .filter((row) => !claimed.has(row.symbol))
                .sort((a, b) => b.amount - a.amount);
            if (unclassified.length > 0) {
                const amount = unclassified.reduce((sum, row) => sum + row.amount, 0);
                classRows.push({
                    label: UNCLASSIFIED_CLASS,
                    amount,
                    amountDisplay: formatCurrency(amount),
                    share: formatPercentOfTvl(amount, reportedReservesTotal),
                    assets: unclassified
                });
            }

            classRows.sort((a, b) => b.amount - a.amount);

            return {
                locationGroups: locationGroupsResult,
                locationSegments: buildSegments(locationGroupsResult),
                makeupGroups: makeupGroupsResult,
                makeupSegments: buildSegments(makeupGroupsResult),
                reserveClasses: classRows,
                reportedReserves: reportedReservesTotal
            };
        }, [
            protocolData?.custody,
            protocolData?.reserveFund,
            protocolData?.vault,
            protocolData?.yieldDistributors,
            protocolTvlNumber,
            offApiPositions
        ]);

    return {
        collateralRatio,
        protocolTvl: protocolTvlFormatted,
        protocolTvlNumber,
        mintedDisplay,
        stakedDisplay,
        stakedNumber,
        reportedReserves,
        // Floored, not rounded: on a reserves page a figure should never
        // read higher than what is actually there.
        reportedReservesDisplay: reportedReserves > 0 ? formatCurrency(Math.floor(reportedReserves), 0, 0) : "—",
        reserveClasses,
        locationSegments,
        locationGroups,
        makeupSegments,
        makeupGroups,
        isLoading: isLoading || isStatsLoading
    };
}
